import { createListenerMiddleware } from '@reduxjs/toolkit';

import { PopupType } from '../../components/popup/types';
import { fetchPlayerConfigSyncAction } from '../apiActions/fetchPlayerConfigSyncAction';
import { fetchPromoPointsAction } from '../apiActions/fetchPromotionAction';
import type { CoreRootState } from '../CoreStore';
import { configActions } from '../slices/config/slice';
import { popupActions } from '../slices/popup/slice';

export const promotionsMiddleware = createListenerMiddleware();

// Listen for fetchPromoPointsAction.fulfilled
promotionsMiddleware.startListening({
  actionCreator: fetchPromoPointsAction.fulfilled,
  effect: (action, { dispatch, getState }) => {
    const state = getState() as CoreRootState;
    const session = state.config.session;

    if (!session) return;
    const { JSESSIONID, metaServer: url, gametype } = session;
    if (!(JSESSIONID && url)) return;

    const promoPointsData = action.payload;
    const playerConfig = state.config.playerConfig;

    // Separate tournamentsIRLEData, prizedropsIRLEData, and cashbacksIRLEData
    const tournamentsIRLEData = playerConfig?.tournamentsIRLEData || '';
    const prizedropsIRLEData = playerConfig?.prizedropsIRLEData || '';
    const cashbacksIRLEData = playerConfig?.cashbacksIRLEData || '';

    const tournamentsObj: Record<string, number> = updateDataObj(tournamentsIRLEData);
    const prizedropsObj: Record<string, number> = updateDataObj(prizedropsIRLEData);
    const cashbacksObj: Record<string, number> = updateDataObj(cashbacksIRLEData);

    const combinedBody: Record<string, string> = {};

    const typeMap = {
      TOURNAMENT: {
        dataObj: tournamentsObj,
        data: tournamentsIRLEData,
        configAction: configActions.setPlayerConfigTournamentsIRLEData,
        bodyKey: 'tms_data',
      },
      PRIZEDROP: {
        dataObj: prizedropsObj,
        data: prizedropsIRLEData,
        configAction: configActions.setPlayerConfigPrizedropsIRLEData,
        bodyKey: 'pds_data',
      },
      CASHBACK: {
        dataObj: cashbacksObj,
        data: cashbacksIRLEData,
        configAction: configActions.setPlayerConfigCashbacksIRLEData,
        bodyKey: 'cbs_data',
      },
    };
    for (const [promoId, promoPointsItem] of Object.entries(promoPointsData)) {
      const typeConfig = typeMap[promoPointsItem.type];

      if (typeConfig) {
        if (promoPointsItem.isRoundLimitExceeded && !(promoId in typeConfig.dataObj)) {
          const res = getPromotionName(promoId, state);

          if (res.length > 0 && res[0]) {
            dispatch(popupActions.setCustomTitleForPopup(res[0]));
            dispatch(popupActions.pushPopup(PopupType.PromoRoundLimit));

            const newData =
              typeConfig.data.length === 0 ? `${promoId}=${res[1]}` : `${typeConfig.data},${promoId}=${res[1]}`;

            combinedBody[typeConfig.bodyKey] = newData;

            // Update state of playerConfig data
            dispatch(typeConfig.configAction(newData));

            // Dismiss the popup after 3000ms (3 seconds)
            setTimeout(() => {
              dispatch(popupActions.dismissPopup(PopupType.PromoRoundLimit));
            }, 3000);
          }
        }
      }
    }

    if (Object.keys(combinedBody).length > 0) {
      dispatch(
        fetchPlayerConfigSyncAction({
          JSESSIONID,
          ck: Date.now().toString(),
          url: `${url}/api/ui/savePlayerConfigGeneric`,
          body: { ...combinedBody, gameType: gametype ?? '' },
        })
      );
    }
  },
});

export function getPromotionName(key: string, state: CoreRootState): [string, number] | [] {
  let name: string = '';
  let endTime: number = 0;

  for (const promo of state.promotions.activePromos) {
    if (promo.tid === Number(key) || promo.pdid === Number(key) || promo.cbid === Number(key)) {
      name = promo.nm;
      endTime = promo.ed;

      // Remove '#DDW#' 'IDNLCSM' if present in the name
      if (name.indexOf('#DDW#') > -1) {
        name = name.replace('#DDW#', '');
      } else if (name.indexOf('#IDNLCSM#') > -1) {
        name = name.replace('#IDNLCSM#', '');
      }

      break;
    }
  }

  // Check for undefined before returning the result
  if (name && endTime) {
    return [name, endTime];
  }

  // Return an empty array if no matching tournament is found
  return [];
}

const updateDataObj = (data: string): Record<string, number> => {
  const dataObj: Record<string, number> = {};

  if (data && data.length) {
    data.split(',').forEach((item) => {
      const [key, value] = item.split('=');
      if (key && value) {
        dataObj[key] = Number(value);
      }
    });
  }

  return dataObj;
};
